// Pure helpers for the "approve your printed comic to start shipping" home-page
// banner + popup. No React / no browser APIs here, so the API route can share it.
// No env access here either — this file is also imported client-side (via
// use-pending-approvals.ts), so the caller (the API route) reads
// COMIX_AUTO_APPROVE_DAYS itself and passes it in.

export interface PendingApprovalBook {
  runId: string;
  storyHeadline: string;
  coverImage: string;
  purchasedAt: string; // ISO — display / "newest first" sort only, not the deadline
  deadlineAt: string; // ISO — when the comix-auto-approve cron will finalize this run
  /** A printed copy was bought (copy: "start shipping") vs digital only ("get your book"). */
  printed: boolean;
}

export const DEFAULT_AUTO_APPROVE_DAYS = 4;

/** Milliseconds left until `deadlineIso` (clamped at 0). A book stays "pending" —
 *  and the banner/popup keep showing it — for as long as this is > 0, i.e. until the
 *  customer approves or the auto-approve cron finalizes it for them. There's no
 *  separate fixed window any more: this IS the real auto-approve deadline. */
export function msLeft(deadlineIso: string, now: number): number {
  return Math.max(0, new Date(deadlineIso).getTime() - now);
}

/** `HH:MM:SS` for a millisecond duration. Hours aren't padded past 2 digits — with
 *  the default 7-day deadline this tops out around "168:00:00", not "99:59:59". */
export function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

interface RawOrderRow {
  runId: string | null;
  createdAt: Date;
  product?: string | null;
}
interface RawRunRow {
  id: string;
  config: unknown;
}

/**
 * Pure core of GET /api/comix/pending-approvals: given this session's non-cancelled
 * printed order rows and their runs' configs, produce the card list — one entry per
 * book still awaiting approval (dropped once `userApproved`, or with no cover), newest
 * purchase first. Each book's deadline is `config.approvalRequestedAt` (set
 * automatically at purchase by order-paid, same anchor the comix-auto-approve cron
 * itself uses) plus `autoApproveDays`, falling back to the purchase time itself if
 * that field is somehow missing.
 */
export function buildPendingApprovalBooks(
  orders: RawOrderRow[],
  runs: RawRunRow[],
  autoApproveDays: number = DEFAULT_AUTO_APPROVE_DAYS,
): PendingApprovalBook[] {
  const earliestByRun = new Map<string, Date>();
  const printedRuns = new Set<string>();
  for (const o of orders) {
    if (!o.runId) continue;
    if (o.product === 'printed' || o.product === 'physical20') printedRuns.add(o.runId);
    const prev = earliestByRun.get(o.runId);
    if (!prev || o.createdAt < prev) earliestByRun.set(o.runId, o.createdAt);
  }

  const windowMs = autoApproveDays * 24 * 60 * 60 * 1000;

  return runs
    .map((run): PendingApprovalBook | null => {
      const config = (run.config as Record<string, unknown>) || {};
      if (config.userApproved === true) return null;
      const purchasedAt = earliestByRun.get(run.id);
      const coverImage = typeof config.coverImage === 'string' ? config.coverImage : null;
      if (!purchasedAt || !coverImage) return null;
      const anchor =
        typeof config.approvalRequestedAt === 'string'
          ? new Date(config.approvalRequestedAt)
          : purchasedAt;
      return {
        runId: run.id,
        storyHeadline:
          typeof config.storyHeadline === 'string' ? config.storyHeadline : 'Your comic book',
        coverImage,
        purchasedAt: purchasedAt.toISOString(),
        deadlineAt: new Date(anchor.getTime() + windowMs).toISOString(),
        printed: printedRuns.has(run.id),
      };
    })
    .filter((b): b is PendingApprovalBook => b !== null)
    .sort((a, b) => new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime());
}
